import { CircularProgress, IconButton, IconButtonProps, styled } from '@mui/material';
import React from 'react';

import { iconSlot } from '../icons/iconSlot';

export type BaseIconButtonProps = Omit<
    IconButtonProps,
    'size' | 'href' | 'aria-label' | 'loading' | 'loadingIndicator'
> & {
    size?: 'small' | 'medium' | 'large';
    /** Use this, not MUI's `loading`: this theme draws MUI's spinner almost white. */
    isLoading?: boolean;
    /**
     * The button's name for screen readers, which skip its icon: the action, translated. Usually the tooltip's text,
     * but still the action when the tooltip describes a state or why the button is disabled. It replaces the name a
     * Tooltip around the button would give; to keep such a tooltip for screen readers, give the Tooltip
     * `describeChild`, which reaches the button only when the button is the Tooltip's direct child.
     */
    'aria-label': string;
};

export const BaseIconButton = React.forwardRef<HTMLButtonElement, BaseIconButtonProps>(
    ({ size = 'large', isLoading = false, ...props }, ref) => {
        switch (size) {
            case 'small':
                return (
                    <SmallButton
                        {...props}
                        children={isLoading ? <CircularProgress size={'auto'} /> : props.children}
                        disabled={isLoading || props.disabled}
                        ref={ref}
                    />
                );
            case 'medium':
                return (
                    <MediumButton
                        {...props}
                        children={isLoading ? <CircularProgress size={'auto'} /> : props.children}
                        disabled={isLoading || props.disabled}
                        ref={ref}
                    />
                );
            case 'large':
                return (
                    <LargeButton
                        {...props}
                        children={isLoading ? <CircularProgress size={'auto'} /> : props.children}
                        disabled={isLoading || props.disabled}
                        ref={ref}
                    />
                );
        }
    },
);

/** Link mode of the icon button variants; while loading it drops the router's `href` and click. */
export const LinkIconButton = React.forwardRef<HTMLButtonElement, BaseIconButtonProps & { href?: string }>(
    ({ href, onClick, isLoading, ...props }, ref) => (
        <BaseIconButton {...props} {...(isLoading ? {} : { href, onClick })} isLoading={isLoading} ref={ref} />
    ),
);

const SmallButton = styled(IconButton)({
    width: '24px',
    height: '24px',
    fontSize: '16px',
    // The theme's `padding: 11px` leaves almost no content box, which would squeeze a Lucide svg.
    '& svg': {
        flexShrink: 0,
    },
    // Sizes an icon placed directly or in one wrapper, such as a span or a Badge; deeper svgs keep their own size.
    ...iconSlot(16, { reach: 'wrapped' }),
    // The loading spinner isn't an icon to the slot, so it gets its size here.
    '& > .MuiCircularProgress-root > svg': {
        width: '16px',
        height: '16px',
    },
});

const MediumButton = styled(IconButton)({
    height: '32px',
    width: '32px',
    fontSize: '16px',
    '& svg': {
        flexShrink: 0,
    },
    ...iconSlot(16, { reach: 'wrapped' }),
    '& > .MuiCircularProgress-root > svg': {
        width: '16px',
        height: '16px',
    },
});

const LargeButton = styled(IconButton)({
    fontSize: '18px',
    lineHeight: '20px',
    width: '40px',
    height: '40px',
    '& svg': {
        flexShrink: 0,
    },
    ...iconSlot(18, { reach: 'wrapped' }),
    '& > .MuiCircularProgress-root > svg': {
        width: '20px',
        height: '20px',
    },
});
